import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { TypedMemo } from '../../../../common/types';
import { Panel, PanelBody, PanelFoot, PanelHead } from '../../../components/primitives/Panel';
import { SMLButton } from '../../../components/primitives/SMLButton';
import { cardPicture, groupRevisionCards, newCards, RevisionCard } from '../assets/revisionCards';
import { kitPictureSrc } from '../../kitPictureSrc';
import { revisionCardLabel } from '../assets/cardLabel';
import { KitZip } from '../assets/useKitZip';
import { releaseMessages } from '../releaseMessages';
import { ReleaseTab } from '../releaseTabProps';
import { overviewMessages, revisionGroupMessages } from './overviewMessages';
/* eslint-disable css-modules/no-unused-class -- a shared stylesheet; each part uses its own slice of it */
import styles from './Overview.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface ContentMadePanelProps {
  cards: RevisionCard[];
  /** The base time of "Just made" (this user's last view, else 7 days back). */
  since: string;
  zip: KitZip;
  onOpenTab: (tab: ReleaseTab) => void;
}

const JUST_MADE_MAX = 3;
const STRIP_MAX = 4;
/** The CSS width of the pictures of the panel (small strip tiles). */
const PICTURE_PX = 120;

const Picture: React.FC<{ card: RevisionCard; className?: string }> = ({ card, className }) => {
  const picture = cardPicture(card);
  return picture === null ? (
    <span className={className} />
  ) : (
    <img className={className} src={kitPictureSrc(picture, PICTURE_PX)} alt="" />
  );
};

/**
 * Content made (design b1): the newest revision of each asset, "Just made" (made since this user
 * last opened the release, at most 3, newest first) and the asset types with their counts. A
 * click opens the Kit tab. "Download all (.zip)" holds the newest ready revision of every asset.
 */
const ContentMadePanelComponent: React.FC<ContentMadePanelProps> = ({ cards, since, zip, onOpenTab }) => {
  const intl = useIntl();
  const fresh = useMemo(() => newCards(cards, since), [cards, since]);
  const groups = useMemo(() => groupRevisionCards(cards), [cards]);
  const hasReady = useMemo(() => cards.some((card) => card.state === 'ready' || card.readyAsset !== null), [cards]);
  const openAssets = (): void => onOpenTab('kit');
  // The tiles are boxes (Panel) that act as buttons: Enter and Space open the Assets tab too.
  const onKey = (event: React.KeyboardEvent): void => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      openAssets();
    }
  };

  return (
    <Panel as="section" data-testid="release-content-made">
      <PanelHead
        title={intl.formatMessage(overviewMessages.madeTitle)}
        subtitle={intl.formatMessage(overviewMessages.madeSubtitle)}
        right={
          <span className={styles.meta}>
            {intl.formatMessage(overviewMessages.madeMeta, { count: cards.length })}
            {fresh.length > 0 && <span className={styles.newBadge}>{intl.formatMessage(overviewMessages.newTag)}</span>}
            <button type="button" className={styles.textLink} onClick={openAssets}>
              {intl.formatMessage(overviewMessages.seeAll)}
            </button>
          </span>
        }
      />
      <PanelBody>
        {cards.length === 0 && (
          <span className={styles.footText}>{intl.formatMessage(overviewMessages.nothingMade)}</span>
        )}
        {fresh.length > 0 && (
          <>
            <div className={styles.subHead}>
              {intl.formatMessage(overviewMessages.justMade)}
              <span className={styles.subHeadNote}>{intl.formatMessage(overviewMessages.justMadeSub)}</span>
            </div>
            <div className={styles.justMade}>
              {fresh.slice(0, JUST_MADE_MAX).map((card) => (
                <Panel
                  nested
                  key={card.key}
                  className={styles.jmCard}
                  role="button"
                  tabIndex={0}
                  onClick={openAssets}
                  onKeyDown={onKey}
                >
                  <Picture card={card} className={styles.jmThumb} />
                  <span className={styles.jmText}>
                    <b className={styles.jmName}>{revisionCardLabel(intl, card)}</b>
                    <span className={styles.jmLine}>
                      {`${intl.formatMessage(overviewMessages.revision, { revision: card.revision })} · ${intl.formatTime(new Date(card.createdAt))}`}
                      <span className={styles.newBadge}>{intl.formatMessage(overviewMessages.newTag)}</span>
                    </span>
                  </span>
                </Panel>
              ))}
            </div>
          </>
        )}
        {groups.length > 0 && (
          <>
            <div className={styles.subHead}>{intl.formatMessage(overviewMessages.byType)}</div>
            <div className={styles.types}>
              {groups.map((group) => (
                <Panel
                  nested
                  key={group.id}
                  className={styles.typeTile}
                  role="button"
                  tabIndex={0}
                  onClick={openAssets}
                  onKeyDown={onKey}
                >
                  <span className={styles.typeHead}>
                    {intl.formatMessage(revisionGroupMessages[group.id])}
                    <span className={styles.typeCount}>{group.cards.length}</span>
                  </span>
                  <span className={styles.strip}>
                    {group.cards.slice(0, STRIP_MAX).map((card) => (
                      <Picture key={card.key} card={card} className={styles.stripThumb} />
                    ))}
                  </span>
                </Panel>
              ))}
            </div>
          </>
        )}
      </PanelBody>
      {hasReady && (
        <PanelFoot>
          <span className={styles.footText}>{intl.formatMessage(overviewMessages.zipNote)}</span>
          {zip.readyUrl === null ? (
            <SMLButton disabled={zip.isZipping} onClick={zip.download}>
              {intl.formatMessage(releaseMessages.downloadAll)}
            </SMLButton>
          ) : (
            <SMLButton href={zip.readyUrl}>{intl.formatMessage(releaseMessages.downloadZip)}</SMLButton>
          )}
        </PanelFoot>
      )}
    </Panel>
  );
};

export const ContentMadePanel = TypedMemo(ContentMadePanelComponent);
